/**
 * Apnea screening on an individual recording (project_management#555).
 *
 * The backend (`Recording.apneaScreening`) already slices the SEQUENCE's
 * per-minute screening to this recording's samples, and returns no minutes
 * when the sequence is not assessable. These helpers only decide what the
 * recording view shows — kept pure so the wording rules are unit-tested.
 */

export interface RecordingApneaMinute {
  startIndex: number;
  endIndex: number;
  probability: number | null;
  partial: boolean;
}

export interface RecordingApneaScreening {
  sequenceId: string;
  assessable: boolean;
  windowDurationSec: number;
  analysedMinutes: number | null;
  apneaMinutes: number | null;
  minutes: RecordingApneaMinute[];
  modelVersion?: string | null;
}

export type ApneaNotice =
  | { key: 'recordingApnea.notAssessable' }
  | { key: 'recordingApnea.noScreenedMinutes' }
  | { key: 'recordingApnea.noneInRecording'; analysed: number }
  | { key: 'recordingApnea.assessable'; apnea: number; analysed: number };

/**
 * Which sentence the recording view shows. Never a per-recording burden or
 * severity — only what this recording contributes to the sequence screening.
 * Null (no screening for this recording, or the feature is off) shows nothing.
 */
export function apneaNotice(screening: RecordingApneaScreening | null | undefined): ApneaNotice | null {
  if (!screening) return null;
  if (!screening.assessable) return { key: 'recordingApnea.notAssessable' };

  const analysed = screening.analysedMinutes ?? 0;
  const apnea = screening.apneaMinutes ?? 0;

  if (analysed === 0) return { key: 'recordingApnea.noScreenedMinutes' };
  if (apnea === 0) return { key: 'recordingApnea.noneInRecording', analysed };

  return { key: 'recordingApnea.assessable', apnea, analysed };
}

/** Minutes overlapping the visible sample window [from, to]. */
export function visibleApneaMinutes(
  minutes: RecordingApneaMinute[] | null | undefined,
  [from, to]: number[],
): RecordingApneaMinute[] {
  return (minutes ?? []).filter(({ startIndex, endIndex }) => endIndex > from && startIndex < to);
}

export interface ApneaBand {
  startIndex: number;
  endIndex: number;
  /** Consecutive apnea-pattern minutes merged into this band. */
  minutes: number;
}

/**
 * Merge back-to-back apnea-pattern minutes into one band. Apnea minutes come in
 * runs; one band per minute stacks a label every minute, which overlap as soon
 * as the view shows more than a couple of minutes.
 */
export function apneaBands(minutes: RecordingApneaMinute[] | null | undefined): ApneaBand[] {
  const sorted = [...(minutes ?? [])].sort((a, b) => a.startIndex - b.startIndex);
  const bands: ApneaBand[] = [];

  sorted.forEach(({ startIndex, endIndex }) => {
    const last = bands[bands.length - 1];

    if (last && startIndex <= last.endIndex) {
      last.endIndex = Math.max(last.endIndex, endIndex);
      last.minutes += 1;
    } else {
      bands.push({ startIndex, endIndex, minutes: 1 });
    }
  });

  return bands;
}

/** In-app page holding the sequence's screening (the apnea card lives there). */
export const sequenceReportPath = (sequenceId: string): string => `/recording-sequences/${sequenceId}`;
